import React, { useEffect, useState, useMemo } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";

//import Error from "Components/Error";
import Button from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";
import useInput from "Hooks/useInput";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentSelector } from "Reducers/environment";
import { initializeVariable } from "Reducers/environment/settings/variable/thunks/initializeVariable.thunk";
import { selectProjectProvisioningWizardEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { gitProjectSelector, updateProject } from "Reducers/project/project";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./InitializeWizard.styles";
import Credentials from "./steps/Credentials";
import ImportYourCode from "./steps/ImportYourCode";
import ProjectTitle from "./steps/ProjectTitle";
import Template from "./steps/Template";

const InitializeWizard = () => {
  const intl = useIntl();

  const StartData = ["template", "existingCode"];

  const initializeData = {
    repository: "https://github.com/magento/magento-cloud.git@master",
    mode: 436,
    profile: "Magento Enterprise Cloud Edition",
    path: "auth.json"
  };

  const [closedWizard, setClosedWizard] = useState(false);
  const [start, setStart] = useState(StartData[0]);
  //const [initializeError, setInitializeError] = useState(null);

  const { inputs, handleInputChange, setInputs } = useInput({
    title: "",
    username: "",
    password: ""
  });
  const dispatch = useAppDispatch();
  const { organizationId, projectId } = useDecodedParams();

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const environment = useAppSelector(state =>
    environmentSelector(state, {
      organizationId,
      projectId,
      environmentId: project?.default_branch
    })
  );

  const enableProjectProvisioningWizard = useAppSelector(
    selectProjectProvisioningWizardEnabled
  );

  const initializeAndEditPermission = useMemo(
    () =>
      environment?.hasPermission("#initialize") &&
      project?.hasPermission("#edit"),
    [environment, project]
  );

  useEffect(() => {
    if (project?.title) {
      setInputs(inputs => ({ ...inputs, ...{ title: project.title } }));
    }
  }, [project?.title, setInputs]);

  const getInitializedEnvironment = async () => {
    const content = {
      "http-basic": {
        "repo.magento.com": {
          username: inputs.username,
          password: inputs.password
        }
      }
    };

    const base64Content = window.btoa(JSON.stringify(content));

    const environmentOptions = {
      repository: initializeData.repository,
      profile: initializeData.profile,
      files: [
        {
          mode: initializeData.mode,
          path: initializeData.path,
          contents: base64Content
        }
      ]
    };

    await dispatch(
      initializeVariable({
        project,
        title: inputs.title,
        environmentOptions,
        environment
      })
    );
  };

  const closeWizard = () => {
    setClosedWizard(true);
  };

  const saveTitle = async e => {
    e.preventDefault();
    if (!project?.id) return;

    await dispatch(
      updateProject({
        organizationDescriptionId: organizationId,
        projectDescriptionId: projectId,
        data: {
          title: inputs.title,
          timezone: project.timezone // FIXME
        }
      })
    );

    getInitializedEnvironment();

    closedWizard(true);
  };

  if (closedWizard) return null;

  if (!initializeAndEditPermission || !enableProjectProvisioningWizard)
    return null;

  const handleStartStateChange = newState => {
    setStart(newState);
  };

  return (
    <S.Wrapper>
      <Typography.Title tag="h2">
        {intl.formatMessage({
          id: "wizard.project.provisioning.setting.project"
        })}
      </Typography.Title>
      <br />
      <ProjectTitle inputs={inputs} handleInputChange={handleInputChange} />
      <br />
      <Template onStartChange={handleStartStateChange} />
      <br />
      {start === StartData[0] ? (
        <Credentials inputs={inputs} handleInputChange={handleInputChange} />
      ) : (
        <ImportYourCode url={project?.repository?.url} />
      )}

      <br />
      <br />
      <form>
        <S.ButtonWrapper>
          <Button
            analyticId="wizard.finish"
            content="text"
            onClick={saveTitle}
            disabled={
              inputs.title === "" ||
              inputs.username === "" ||
              inputs.password === ""
            }
          >
            {intl.formatMessage({ id: "wizard.finish" })}
          </Button>

          <Button
            content="text"
            onClick={closeWizard}
            variant="secondary"
            analyticId="wizard.project.provisioning.continue"
          >
            {intl.formatMessage({ id: "wizard.project.provisioning.continue" })}
          </Button>
        </S.ButtonWrapper>
      </form>
    </S.Wrapper>
  );
};

export default InitializeWizard;
